import type { DeliveredFeedback, Ticket, TicketChange } from '../api/types.js';
import { Part } from './ticket-epic.js';
import { PlainText } from './plain-text.js';
import { splitDiff, type DiffRow as Row } from '../utils/unified-diff.js';

// The change of a ticket (ADR-026 step 2), as the API read it from the session's commit: what Open the PR pushes. The
// diff is the session's work, shown as plain text in a table (never as HTML), one collapsible file at a time; its
// lines take the colours of the file viewer's diff (diff-viewer.tsx), so a change reads the same everywhere.

const LINE: Record<'added' | 'removed' | 'context', { bg?: string; color: string; mark: string }> = {
  added: { bg: 'var(--color-success-dim)', color: 'var(--color-success)', mark: '+' },
  removed: { bg: 'var(--color-error-dim)', color: 'var(--color-error)', mark: '-' },
  context: { color: 'var(--color-text-2)', mark: ' ' },
};

function FileDiff({ rows }: { rows: Row[] | undefined }) {
  if (!rows) return <p className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-3)' }}>Beyond the part of the diff shown here.</p>;
  if (!rows.length) return <p className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-3)' }}>No line changes (an empty file, or its mode only).</p>;
  const num = 'w-10 select-none border-r px-2 text-right align-top';
  return (
    <div className="max-h-[32rem] overflow-auto text-xs" style={{ fontFamily: 'var(--font-mono)' }}>
      <table className="w-full border-collapse">
        <tbody>
          {rows.map((r, i) => {
            if (r.kind === 'hunk' || r.kind === 'note') {
              return (
                <tr key={i} style={{ backgroundColor: 'var(--color-surface-2)' }}>
                  <td colSpan={3} className="whitespace-pre px-2 py-0.5" style={{ color: 'var(--color-text-3)' }}>{r.text}</td>
                </tr>
              );
            }
            const s = LINE[r.kind];
            return (
              <tr key={i} style={s.bg ? { backgroundColor: s.bg } : undefined}>
                <td className={num} style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-3)' }}>{r.oldNo ?? ''}</td>
                <td className={num} style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-3)' }}>{r.newNo ?? ''}</td>
                <td className="whitespace-pre px-2 py-px" style={{ color: s.color }}>
                  <span className="select-none" aria-hidden="true">{s.mark} </span>
                  <span className="sr-only">{r.kind === 'added' ? 'Added: ' : r.kind === 'removed' ? 'Removed: ' : ''}</span>
                  {r.text}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_WORD: Record<TicketChange['files'][number]['status'], string> = { added: 'new', modified: 'changed', deleted: 'deleted' };

/** One file per row, collapsed when the change is long: the operator opens the ones to read */
function Files({ change }: { change: TicketChange }) {
  const byPath = splitDiff(change.diff);
  const openAll = change.files.length <= 3;
  const added = change.files.reduce((n, f) => n + (f.additions ?? 0), 0);
  const removed = change.files.reduce((n, f) => n + (f.deletions ?? 0), 0);
  return (
    <Part title={`Files (${change.files.length}): +${added} −${removed}`}>
      <ul className="space-y-1.5">
        {change.files.map((f) => (
          <li key={f.path}>
            <details open={openAll} className="group min-w-0 overflow-hidden rounded-md border" style={{ borderColor: 'var(--color-border)' }}>
              <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-2 px-3 py-1.5 text-xs focus-visible:outline focus-visible:outline-2 max-lg:min-h-10 max-lg:py-2.5"
                style={{ backgroundColor: 'var(--color-surface-2)' }}>
                <span className="min-w-0 break-all" style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>{f.path}</span>
                <span style={{ color: 'var(--color-text-3)' }}>{STATUS_WORD[f.status]}</span>
                {f.additions === null
                  ? <span style={{ color: 'var(--color-text-3)' }}>binary</span>
                  : (
                    <span className="ml-auto tabular-nums">
                      <span style={{ color: 'var(--color-success)' }}>+{f.additions}</span>{' '}
                      <span style={{ color: 'var(--color-error)' }}>−{f.deletions}</span>
                    </span>
                  )}
              </summary>
              <FileDiff rows={byPath.get(f.path)} />
            </details>
          </li>
        ))}
      </ul>
      {change.diffTruncated && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-warn)' }}>
          The diff is too long to show in full: it stops here. All the files are pushed; open the PR as a draft and read the rest on GitHub.
        </p>
      )}
    </Part>
  );
}

const RESULT: Record<'passed' | 'failed' | 'skipped', { word: string; color: string; bg: string }> = {
  passed: { word: 'Passed', color: 'var(--color-success)', bg: 'var(--color-success-dim)' },
  failed: { word: 'Failed', color: 'var(--color-error)', bg: 'var(--color-error-dim)' },
  skipped: { word: 'Not run', color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
};

function Tests({ tests }: { tests: TicketChange['tests'] }) {
  if (!tests.length) {
    return <Part title="Tests"><p className="text-xs" style={{ color: 'var(--color-warn)' }}>The session reported no test or lint run.</p></Part>;
  }
  return (
    <Part title="Tests and linters, as the session ran them">
      <ul className="space-y-1.5">
        {tests.map((r, i) => {
          const s = RESULT[r.passed === true ? 'passed' : r.passed === false ? 'failed' : 'skipped'];
          return (
            <li key={i} className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-xs">
              <span className="rounded px-1.5 py-px font-medium" style={{ color: s.color, backgroundColor: s.bg }}>{s.word}</span>
              <code className="break-all" style={{ color: 'var(--color-text)' }}>{r.command}</code>
              {r.summary && <span className="basis-full pl-0.5 sm:basis-auto" style={{ color: 'var(--color-text-2)' }}>{r.summary}</span>}
            </li>
          );
        })}
      </ul>
    </Part>
  );
}

const short = (sha: string) => sha.slice(0, 7);

/** The comment a reply to each thread answers: the thread's first one delivered, which asked the question */
export function feedbackByThread(feedback: DeliveredFeedback[] | undefined): Map<number, DeliveredFeedback> {
  const by = new Map<number, DeliveredFeedback>();
  for (const f of feedback ?? []) if (!by.has(f.thread)) by.set(f.thread, f);
  return by;
}

/** Who wrote the comment a reply answers, and where */
export function feedbackPlace(f: DeliveredFeedback): string {
  return f.kind === 'line' ? `${f.author} on ${f.path}${f.line ? `:${f.line}` : ''}` : f.kind === 'review' ? `${f.author}'s review` : `${f.author} in the conversation`;
}

/**
 * The session's replies to the pull request's comments (ADR-035 step 3), each under the comment it answers: drafted for
 * the operator, who edits and posts the ones he keeps (Push the update, or Post the replies); a posted one links to it
 */
function Replies({ t }: { t: Ticket }) {
  const replies = t.change?.replies ?? [];
  if (!replies.length) return null;
  const by = feedbackByThread(t.feedback);
  const posted = new Map((t.repliesPosted ?? []).map((r) => [r.to, r] as const));
  return (
    <Part title={`Replies to the pull request's comments (${replies.length})`}>
      <ul className="space-y-3">
        {replies.map((r) => {
          const f = by.get(r.to);
          const p = posted.get(r.to);
          return (
            <li key={r.to} className="space-y-1 text-xs">
              <p style={{ color: 'var(--color-text-3)' }}>
                {f ? feedbackPlace(f) : `Thread ${r.to}`}
                {p && (
                  <>
                    {' · '}
                    <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>posted</a>
                  </>
                )}
              </p>
              {f && <blockquote className="border-l-2 pl-2" style={{ borderColor: 'var(--color-border-2)' }}><PlainText text={f.excerpt} className="text-xs" color="var(--color-text-2)" /></blockquote>}
              <PlainText text={r.body} className="text-xs" />
            </li>
          );
        })}
      </ul>
    </Part>
  );
}

/** The change the operator decides on: where it starts, its commits, its tests, its files */
export function ChangeView({ t }: { t: Ticket }) {
  const c = t.change;
  if (!c) return null;
  const behind = c.baseHead && c.baseHead !== c.base;
  return (
    <section className="space-y-5 border-t pt-5" style={{ borderColor: 'var(--color-border)' }} aria-label={`The change, version ${c.version}`}>
      <div>
        <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          {c.version > 1 ? `The change, version ${c.version}` : 'The change'}
        </h4>
        <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
          Commit <code>{short(c.commit)}</code>, from <code>{c.baseBranch}</code> at <code>{short(c.base)}</code>
          {c.commits.length > 1 ? `, ${c.commits.length} local commits pushed as one` : ''}.
        </p>
        {behind && (
          <p className="mt-1 text-xs" style={{ color: 'var(--color-warn)' }}>
            {c.baseBranch} has moved on since (<code>{short(c.baseHead)}</code>): the PR can be opened, and its description will say the branch is behind.
          </p>
        )}
        {c.warnings.map((w) => <p key={w} className="mt-1 text-xs" style={{ color: 'var(--color-warn)' }}>{w}.</p>)}
      </div>
      <Tests tests={c.tests} />
      <Files change={c} />
      <Replies t={t} />
      {c.leftOut?.map((l) => (
        <Part key={l.field} title={l.field === 'body' ? 'Description left out by the checks' : `Reply left out by the checks (${l.field})`}>
          <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>It {l.reason}{l.field === 'body' ? ': write the description yourself when opening the PR' : ''}.</p>
          <div className="mt-1"><PlainText text={l.content} className="text-xs" color="var(--color-text-3)" /></div>
        </Part>
      ))}
    </section>
  );
}

/** A change.json the checks refused: the operator asks for changes or drops */
export function ChangeErrors({ t }: { t: Ticket }) {
  if (t.status !== 'implementing' || !t.changeErrors?.length) return null;
  return (
    <p role="alert" className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
      The session&apos;s change can&apos;t be used: {t.changeErrors.join('; ')}. Ask for changes to get a new version, or drop it.
    </p>
  );
}
